import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { Booking, RateType } from '../../../core/models/entities/booking';
import { ModalService } from '../../../core/services/modal.service';
import { Store } from '@ngrx/store';
import { ToastService } from 'angular/app/core/services/toast.service';
import { roomsActions } from 'angular/app/core/store/rooms';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { TranslocoService } from '@jsverse/transloco';
import {
  BookingDurationType,
  bookingFormActions,
} from '../../../core/store/booking-form';
import { BookingType } from '../../../core/enums/booking-type';
import { PublicBookingComponent } from '../../../shared/components/public-booking/public-booking.component';
import { formatISO, parseISO, startOfDay, subDays } from 'date-fns';
import { DatetimeHelper } from 'angular/app/core/helpers/datetime.helper';
import { fromStore } from 'angular/app/core/store';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Invoice } from '../../../core/models/entities/invoice';
import { BookingsService } from '../../../core/services/bookings.service';
import { TranslatedPipe } from '../../../core/pipes/translated.pipe';
import { ApiGetSuccessResponse } from '../../../core/models/common/api-get-success-response';

@Component({
  selector: 'app-booking-edit-modal',
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="onOk()">
      @if (loading()) {
        <app-spacer size="8"></app-spacer>
        <div class="text-center">
          <app-loader></app-loader>
        </div>
        <app-spacer size="8"></app-spacer>
      } @else if (booking()) {
        @if (infoMessage()) {
          <app-alert>
            <div [innerHtml]="infoMessage()"></div>
          </app-alert>
        }

        @if (showRecurrenceInfoMessage()) {
          <div [class.mt-4]="infoMessage()">
            <app-alert>
              <p>{{ 'bookings.public-edit.recurrence-warning.intro' | transloco }}</p>
              <p [innerHTML]="'bookings.public-edit.recurrence-warning.all-occurrences' | transloco"></p>
              <p>{{ 'bookings.public-edit.recurrence-warning.single-date' | transloco }}</p>
              <p>{{ 'bookings.public-edit.recurrence-warning.no-invoice-change' | transloco }}</p>
            </app-alert>
          </div>
        }

        @if (!booking().automatic_invoicing_enabled) {
          <div [class.mt-4]="infoMessage() || showRecurrenceInfoMessage()">
            <app-manual-invoicing-warning
              [editMode]="true"
            ></app-manual-invoicing-warning>
          </div>
        }

        <app-public-booking
          #publicBookingComponent
          [originalBooking]="booking()"
          (bookingSaved)="saved($event)"
        ></app-public-booking>
      } @else if (error()) {
        <app-alert variant="danger">{{ error() }}</app-alert>
      }
    </app-modal>
  `,
  standalone: false,
})
export class PublicBookingEditModalComponent
  extends ModalBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('publicBookingComponent')
  publicBookingComponent: PublicBookingComponent;
  booking: WritableSignal<Booking | null> = signal(null);
  loading = signal(false);
  error = signal<string | null>(null);
  bookingSaved = output<void>();
  room$ = this.store.select(fromStore.selectBookingFormRoom);
  onDestroy$ = new Subject<void>();
  bookingService = inject(BookingsService);
  translatedPipe = inject(TranslatedPipe);
  infoMessage: WritableSignal<string | null> = signal(null);
  showRecurrenceInfoMessage: WritableSignal<boolean> = signal(false);

  private translocoService = inject(TranslocoService);

  constructor(
    private modalService: ModalService,
    private toastService: ToastService,
    private store: Store,
  ) {
    super();
  }

  async ngOnInit() {
    const focusedBooking: Booking = this.store.selectSignal(
      fromStore.selectFocusedBooking,
    )();
    if (focusedBooking.recurrence_rule) {
      this.loading.set(true);
      await this.setBookingWithFirstRecurrenceBooking(focusedBooking);
      if (this.booking()) {
        this.showRecurrenceInfoMessage.set(true);
        this.initForm();
        await this.loadRecurrenceExceptions();
      } else {
        this.error.set(this.translocoService.translate('bookings.public-edit.error.load-failed'));
      }
      this.loading.set(false);
    } else {
      this.booking.set(focusedBooking);
      this.initForm();
    }

    this.modalInstance.config.update((config) => ({
      ...config,
      alignTop: true,
    }));
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * On modifie toute la récurrence dans ce cas, du coup on se place à la 1re date de la récurrence
   * Pas trouvé de manière plus élégante de faire ça
   * @param booking
   */
  async setBookingWithFirstRecurrenceBooking(booking: Booking) {
    const response: ApiGetSuccessResponse<Booking[]> = await firstValueFrom(
      this.bookingService.findAll({ search: booking.reference }),
    );
    const bookings: Booking[] = response.data;
    const bookingsSortedByStartAsc = bookings
      .filter(
        (loopBooking: Booking) => loopBooking.group_id === booking.group_id,
      )
      .sort((a, b) => a.start.localeCompare(b.start));
    const bookingResponse: ApiGetSuccessResponse<Booking> =
      await firstValueFrom(
        this.bookingService.view(bookingsSortedByStartAsc[0].id),
      );
    this.booking.set(bookingResponse.data);
  }

  private normalizedStartEnd(): { start: string; end: string } {
    const booking = this.booking();
    if (booking.duration.rate !== RateType.Daily) {
      return { start: booking.start, end: booking.end };
    }
    const endsNextDay = booking.rooms.some((room) => room.max_end_next_day)
      ? 1
      : 0;
    return {
      start: formatISO(startOfDay(parseISO(booking.start))),
      end: formatISO(startOfDay(subDays(parseISO(booking.end), endsNextDay))),
    };
  }

  private async loadRecurrenceExceptions() {
    const booking = this.booking();
    if (!booking?.recurrence) return;

    const { start, end } = this.normalizedStartEnd();

    try {
      const exceptions =
        await this.bookingService.verifyDateExceptionsForRecurrence({
          room_id: booking.room_id,
          complementary_rooms_ids: (booking.complementary_rooms ?? []).map((r) => r.id),
          all_day: booking.duration.rate !== RateType.Hourly,
          start: DatetimeHelper.dateToApiFormat(parseISO(start), true),
          end: DatetimeHelper.dateToApiFormat(parseISO(end), true),
          is_public: true,
          recurrence: {
            ...booking.recurrence,
            end: booking.recurrence.end
              ? DatetimeHelper.dateToApiFormat(
                  parseISO(booking.recurrence.end),
                  true,
                )
              : booking.recurrence.end,
          },
          booking_id: booking.id,
        });
      this.store.dispatch(
        bookingFormActions.setData({ recurrence_exceptions: exceptions }),
      );
    } catch {
      // Les exceptions sont informatives, on ne bloque pas si l'appel échoue
    }
  }

  initForm() {
    this.modalInstance.config.update((config) => ({
      ...config,
      showActionButtons: false,
    }));
    this.store.dispatch(roomsActions.findOne({ id: this.booking().room.id }));
    const { start, end } = this.normalizedStartEnd();
    let recurrence_end = this.booking().recurrence_end;
    if (
      this.booking().duration.rate === RateType.Daily &&
      this.booking().recurrence_end
    ) {
      const endsNextDay = this.booking().rooms.some(
        (room) => room.max_end_next_day,
      )
        ? 1
        : 0;
      recurrence_end = formatISO(
        startOfDay(
          subDays(parseISO(this.booking().recurrence_end), endsNextDay),
        ),
      );
    }

    this.store.dispatch(
      bookingFormActions.setData({
        id: this.booking().id,
        status: this.booking().status,
        booking_type: this.booking().booking_type,
        room_id: this.booking().room_id,
        start,
        end,
        coupon_code:
          this.booking().invoices?.find((invoice: Invoice) => {
            return invoice.coupon?.code;
          })?.coupon.code ?? null,
        room_amenities_ids:
          this.booking().group_room_amenities?.map((ra) => ra.id) || [],
        amenities_extras_ids:
          this.booking().group_amenities_extras?.map((ae) => ae.id) || [],
        complementary_rooms_ids: this.booking()
          .rooms.filter((room) => room.id !== this.booking().room_id)
          .map((room) => room.id),
        duration_type:
          this.booking().duration.rate === RateType.Hourly
            ? BookingDurationType.Hourly
            : this.booking().duration.quantity === 1
              ? BookingDurationType.FullDay
              : BookingDurationType.MultipleDays,
        recurrence: this.booking().recurrence
          ? {
              ...this.booking().recurrence,
              end: recurrence_end,
            }
          : null,
        accepts_terms: true,

        // Informations sur l'événement
        event_type_id: this.booking().event_type_id,
        attendance: this.booking().attendance,
        comment: this.booking().comment,
        rate_id: this.booking().rate_id,
        custom_fields: this.booking().custom_fields ?? [],

        // Coordonnées du client
        first_name: this.booking().first_name,
        last_name: this.booking().last_name,
        company: this.booking().company,
        address: this.booking().address,
        zip: this.booking().zip,
        city: this.booking().city,
        has_billing_address: this.booking().has_billing_address,
        billing_first_name: this.booking().billing_first_name,
        billing_last_name: this.booking().billing_last_name,
        billing_company: this.booking().billing_company,
        billing_address: this.booking().billing_address,
        billing_zip: this.booking().billing_zip,
        billing_city: this.booking().billing_city,
        email: this.booking().email,
        phone: this.booking().phone,

        // Options de réservation
        booking_options_ids: this.bookingOptionsIdsOfAllRooms(),

        // Dates multiples : reconstituer les TimeBlocks depuis les siblings
        multiple_dates:
          this.booking().booking_type === BookingType.MultipleDates
            ? this.datesOfMainRoom().map((sibling) => ({
                start: sibling.start as string,
                end: sibling.end as string,
                all_day: sibling.all_day,
                duration_type: sibling.all_day
                  ? BookingDurationType.FullDay
                  : BookingDurationType.Hourly,
              }))
            : undefined,
      }),
    );

    this.store.dispatch(bookingFormActions.setStep({ step: 0 }));
    this.store.dispatch(bookingFormActions.setLatestCompletedStep({ step: 3 }));

    this.room$.pipe(takeUntil(this.onDestroy$)).subscribe((room) => {
      if (!room) return;

      const allRoomAmenitiesExtras = room.room_amenities.flatMap(
        (el) => el.amenity_extras,
      );
      const removedAmenities = [];

      for (const roomAmenity of this.booking().room_amenities.filter(
        (el) => !el.included,
      )) {
        if (!room.room_amenities.some((el) => el.id === roomAmenity.id)) {
          removedAmenities.push(roomAmenity.name ?? this.translatedPipe.transform(roomAmenity.amenity, 'name') as string);
        }
      }

      for (const amenityExtra of this.booking().amenities_extras) {
        if (!allRoomAmenitiesExtras.some((el) => el.id === amenityExtra.id)) {
          removedAmenities.push(this.translatedPipe.transform(amenityExtra, 'name') as string);
        }
      }

      if (removedAmenities.length) {
        this.infoMessage.set(
          this.translocoService.translate('bookings.public-edit.removed-amenities', {
            names: removedAmenities.join(', '),
          }),
        );
      } else {
        this.infoMessage.set(null);
      }
    });
  }

  private bookingOptionsIdsOfAllRooms(): number[] {
    const bookingOptions =
      this.booking().group_booking_options ?? this.booking().booking_options ?? [];
    return bookingOptions.map((bookingOption) => bookingOption.id);
  }

  private datesOfMainRoom(): Booking[] {
    return (this.booking().siblings ?? []).filter(
      (sibling) => sibling.room_id === this.booking().room_id,
    );
  }

    saved(booking: Booking) {
    this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
    this.bookingSaved.emit();
    this.modalService.close();
    this.toastService.success(
      this.translocoService.translate('bookings.public-edit.toast.saved', { name: this.booking().name }),
    );
  }

  onOk() {
    this.publicBookingComponent.submit();
  }
}
